import { useIsAdmin, useIsJobillan, useJobillaOrgId } from '@/hooks'
import { downloadBlob } from '@/lib'
import { logger, MainNav } from '@/ui'
import {
	breakdownCsvFilename,
	buildBreakdownCsv,
	DownloadCsvButton,
	FiltersBar,
	OrganisationAnalyticsView,
	WhatsAppAnalyticsSection,
} from '@/ui'
import type { CampaignStatusFilter, FiltersState } from '@/ui'
import {
	organisationAnalyticsRows,
	roleEnum,
	useAnalyticsWhatsAppOrganisationAnalytics,
	useOrganisationAnalyticsRows,
	useOrganisationAnalyticsShow,
} from '@jobilla/nexus-sdk'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

const analyticsSearchSchema = z.object({
	page: z.number().int().min(1).default(1),
	from: z.string().optional(),
	to: z.string().optional(),
	status: z.array(z.enum(['active', 'completed'])).default([]),
	campaign_ids: z.array(z.string().uuid()).default([]),
})

type AnalyticsSearch = z.infer<typeof analyticsSearchSchema>

export const Route = createFileRoute('/_auth/company/$companyId/analytics')({
	beforeLoad: ({ context }) => {
		const { auth } = context
		if (auth.role !== roleEnum.admin && auth.role !== roleEnum.jobillan) {
			throw redirect({ to: '/' })
		}
	},
	validateSearch: analyticsSearchSchema,
	search: {
		middlewares: [({ next, search }) => {
			const cleaned: Record<string, unknown> = { ...search }
			if (cleaned.page === 1) { delete cleaned.page }
			if (!cleaned.from) { delete cleaned.from }
			if (!cleaned.to) { delete cleaned.to }
			if (Array.isArray(cleaned.status) && cleaned.status.length === 0) { delete cleaned.status }
			if (Array.isArray(cleaned.campaign_ids) && cleaned.campaign_ids.length === 0) {
				delete cleaned.campaign_ids
			}
			return next(cleaned)
		}],
	},
	component: RouteComponent,
})

function RouteComponent() {
	const { companyId } = Route.useParams()
	const search = Route.useSearch()
	const navigate = useNavigate()
	const isAdmin = useIsAdmin()
	const isJobillan = useIsJobillan()
	const canAccess = isAdmin || isJobillan
	const jobillaOrgId = useJobillaOrgId()
	const { t } = useTranslation('analytics')

	const overviewFilters = {
		from: search.from,
		to: search.to,
		status: search.status.length > 0 ? search.status : undefined,
		campaignIds: search.campaign_ids.length > 0 ? search.campaign_ids : undefined,
	}

	const { data, isLoading, isError, refetch } = useOrganisationAnalyticsShow(
		{ ...overviewFilters, page: search.page, perPage: 25 },
		{ enabled: canAccess },
	)

	const downloadCsv = async () => {
		try {
			const { data: { data: rows } } = await organisationAnalyticsRows(overviewFilters)
			const csv = buildBreakdownCsv(rows, { t })
			downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), breakdownCsvFilename(new Date()))
			logger.track('Analytics: Exported CSV', {
				category: 'analytics',
				data: { organisation_id: jobillaOrgId, row_count: rows.length },
			})
		} catch (error) {
			logger.error(error, { category: 'analytics', data: { organisationId: jobillaOrgId, ...overviewFilters } })
			throw error
		}
	}

	const {
		data: whatsappData,
		isLoading: whatsappLoading,
		isError: whatsappError,
		error: whatsappErrorDetails,
	} = useAnalyticsWhatsAppOrganisationAnalytics(overviewFilters, { enabled: isJobillan && Boolean(jobillaOrgId) })

	const hasTrackedWhatsAppViewRef = useRef(false)
	const prevWhatsAppRangeRef = useRef<{ from?: string; to?: string } | null>(null)
	useEffect(() => {
		if (!isJobillan || !jobillaOrgId) {
			return
		}

		if (!hasTrackedWhatsAppViewRef.current) {
			logger.track('WhatsApp Analytics: Viewed', {
				category: 'whatsapp-analytics',
				data: {
					organisation_id: jobillaOrgId,
					start_date: search.from ?? null,
					end_date: search.to ?? null,
				},
			})
			hasTrackedWhatsAppViewRef.current = true
			prevWhatsAppRangeRef.current = { from: search.from, to: search.to }
			return
		}

		const previous = prevWhatsAppRangeRef.current
		if (previous && previous.from === search.from && previous.to === search.to) {
			return
		}

		logger.track('WhatsApp Analytics: Date Range Changed', {
			category: 'whatsapp-analytics',
			data: {
				organisation_id: jobillaOrgId,
				previous_start_date: previous?.from ?? null,
				previous_end_date: previous?.to ?? null,
				start_date: search.from ?? null,
				end_date: search.to ?? null,
			},
		})
		prevWhatsAppRangeRef.current = { from: search.from, to: search.to }
	}, [isJobillan, jobillaOrgId, search.from, search.to])

	useEffect(() => {
		if (whatsappError) {
			logger.error(whatsappErrorDetails, {
				category: 'whatsapp-analytics',
				data: {
					organisationId: jobillaOrgId,
					from: search.from,
					to: search.to,
					status: search.status,
					campaignIds: search.campaign_ids,
				},
			})
		}
	}, [whatsappError, whatsappErrorDetails, jobillaOrgId, search.from, search.to, search.status, search.campaign_ids])

	const { data: campaignsData, isLoading: campaignsLoading } = useOrganisationAnalyticsRows(
		{},
		{ enabled: canAccess, staleTime: 5 * 60 * 1000 },
	)

	const campaignOptions = useMemo(
		() => (campaignsData?.data ?? []).map((c) => ({ id: c.id, label: c.title })),
		[campaignsData],
	)

	const setFilters = (patch: Partial<FiltersState>) => {
		navigate({
			to: '/company/$companyId/analytics',
			params: { companyId },
			search: (prev) => ({
				...prev,
				from: 'from' in patch ? patch.from : prev.from,
				to: 'to' in patch ? patch.to : prev.to,
				status: (patch.status ?? prev.status) as AnalyticsSearch['status'],
				campaign_ids: (patch.campaignIds ?? prev.campaign_ids) as string[],
				page: 1,
			}),
			replace: true,
		})
	}

	const clearFilters = () => {
		navigate({
			to: '/company/$companyId/analytics',
			params: { companyId },
			search: () => ({
				page: 1,
				from: undefined,
				to: undefined,
				status: [],
				campaign_ids: [],
			}),
			replace: true,
		})
	}

	const setPage = (page: number) => {
		navigate({
			to: '/company/$companyId/analytics',
			params: { companyId },
			search: (prev) => ({ ...prev, page }),
			replace: true,
		})
	}

	if (!canAccess) {
		return null
	}

	const filtersValue: FiltersState = {
		from: search.from,
		to: search.to,
		status: search.status as readonly CampaignStatusFilter[],
		campaignIds: search.campaign_ids,
	}

	return (
		<MainNav activeMainNavLink="analytics">
			<AnalyticsPageShell>
				<OrganisationAnalyticsView
					data={data}
					isLoading={isLoading}
					isError={isError}
					onRetry={refetch}
					onPageChange={setPage}
					filtersSlot={
						<FiltersBar
							value={filtersValue}
							onChange={setFilters}
							onClear={clearFilters}
							campaignOptions={campaignOptions}
							campaignsLoading={campaignsLoading}
							actions={data && data.breakdown.meta.total > 0
								? <DownloadCsvButton onDownload={downloadCsv} />
								: null}
						/>
					}
				/>
				{isJobillan
					? (
						<div className="px-8 pb-6">
							<WhatsAppAnalyticsSection
								data={whatsappData}
								isLoading={whatsappLoading || !jobillaOrgId}
								isError={whatsappError}
							/>
						</div>
					)
					: null}
			</AnalyticsPageShell>
		</MainNav>
	)
}

function AnalyticsPageShell({ children }: { children: React.ReactNode }) {
	return (
		<div className="bg-grey-50 min-h-[var(--screen-height)]">
			<div className="max-w-[1440px] mx-auto">
				{children}
			</div>
		</div>
	)
}
